/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useMonitoredPipelines.ts
 *     Feature         :   191-pipeline-monitoring-platform
 *     Description     :   React Query hooks for the registry-driven monitoring page:
 *                         registry+health overview, unified cross-process runs, per-key
 *                         runs/trend, run detail, and the pipeline_monitoring_v2 flag.
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchPipelineOverview,
  fetchUnifiedRuns,
  fetchPipelineRuns,
  fetchPipelineTrend,
  fetchPipelineRunDetail,
  fetchPipelineMonitoringV2Flag,
} from '../services/monitoredPipelinesApi';
import type { UnifiedRunListParams } from '../services/monitoredPipelinesApi';
import type { PipelineRunListFilters } from '@shared/types/monitoredPipelines';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const monitoredPipelineKeys = {
  all:      ['monitored-pipelines'] as const,
  flag:     () => [...monitoredPipelineKeys.all, 'v2-flag'] as const,
  overview: () => [...monitoredPipelineKeys.all, 'overview'] as const,
  unified:  (params?: UnifiedRunListParams) => [...monitoredPipelineKeys.all, 'unified', params] as const,
  runs:     (key: string, params?: PipelineRunListFilters) =>
    [...monitoredPipelineKeys.all, 'runs', key, params] as const,
  trend:    (key: string, days: number, instance?: string) =>
    [...monitoredPipelineKeys.all, 'trend', key, days, instance] as const,
  detail:   (key: string | null, batchId: string | null) =>
    [...monitoredPipelineKeys.all, 'detail', key, batchId] as const,
};

// ---------------------------------------------------------------------------
// Feature flag — gates the whole v2 page (T017)
// ---------------------------------------------------------------------------

export function usePipelineMonitoringV2Flag() {
  return useQuery({
    queryKey: monitoredPipelineKeys.flag(),
    queryFn:  fetchPipelineMonitoringV2Flag,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Registry + health overview — polls every 30 seconds (US2 health grid)
// ---------------------------------------------------------------------------

export function usePipelineOverview(enabled = true) {
  return useQuery({
    queryKey: monitoredPipelineKeys.overview(),
    queryFn:  fetchPipelineOverview,
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// Unified cross-process runs explorer (US2)
// ---------------------------------------------------------------------------

export function useUnifiedRuns(params?: UnifiedRunListParams, enabled = true) {
  return useQuery({
    queryKey: monitoredPipelineKeys.unified(params),
    queryFn:  () => fetchUnifiedRuns(params),
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// Per-process runs (kept for parity drill-downs)
// ---------------------------------------------------------------------------

export function usePipelineRuns(key: string | null, params?: PipelineRunListFilters) {
  return useQuery({
    queryKey: monitoredPipelineKeys.runs(key ?? '', params),
    queryFn:  () => fetchPipelineRuns(key as string, params),
    enabled:  key !== null,
  });
}

// ---------------------------------------------------------------------------
// Per-process duration trend
// ---------------------------------------------------------------------------

export function usePipelineTrend(key: string | null, days: number, instance?: string) {
  return useQuery({
    queryKey: monitoredPipelineKeys.trend(key ?? '', days, instance),
    queryFn:  () => fetchPipelineTrend(key as string, days, instance),
    enabled:  key !== null,
  });
}

// ---------------------------------------------------------------------------
// Run detail — enabled only when a run is selected (drawer)
// ---------------------------------------------------------------------------

export function usePipelineRunDetail(key: string | null, batchId: string | null) {
  return useQuery({
    queryKey: monitoredPipelineKeys.detail(key, batchId),
    queryFn:  () => fetchPipelineRunDetail(key as string, batchId as string),
    enabled:  key !== null && batchId !== null,
  });
}
